<script setup lang="ts">
  import { storeToRefs } from 'pinia';
  import { computed, nextTick } from 'vue';
  import { useRouter } from 'vue-router';

  import type { Partner, Task } from '@stuut/api/models';
  import { Button } from '@ui/components/button';
  import { MonetaryText } from '@ui/components/monetary';

  import { PartnerQuickView } from '@/components/partners';
  import { useTaskCollectionStore } from '@/store';

  interface Props {
    isExpanded?: boolean;
    partner?: Partner;
    task?: Task;
  }

  const props = defineProps<Props>();

  const emits = defineEmits<{
    (e: 'toggleFullscreen'): void;
  }>();

  const router = useRouter();
  const taskCollectionStore = useTaskCollectionStore();
  const {
    currentPartner,
    currentTask,
    isBasicTask,
    isGroupView,
    isPartnerGroupView,
    isTaskStackedMode,
    isTaskTypeGroupView,
  } = storeToRefs(taskCollectionStore);

  const componentTask = computed(() => props.task ?? currentTask.value);

  const componentPartner = computed(() => props.partner ?? currentPartner.value);
</script>

<template>
  <div
    class="flex items-center justify-between overflow-hidden border-b px-5 h-10 gap-x-4 bg-background"
    :class="{
      'border-b-[6px] border-gold': componentTask?.isManualCall && !isGroupView,
    }"
  >
    <template v-if="isPartnerGroupView || (isBasicTask && isTaskStackedMode)">
      <div class="flex items-center gap-x-2.5">
        <div class="flex items-center gap-x-2 truncate font-semibold">
          {{ componentPartner?.name }}

          <PartnerQuickView :key="componentPartner?.id" :partner="componentPartner ?? undefined">
            <i
              class="fa-solid fa-up-right-from-square text-blue hover:text-blue-800 text-4xs!"
              @click.stop.prevent="
                () => {
                  const partnerId = componentPartner?.id || currentTask?.partner_id;
                  if (!partnerId) return;

                  taskCollectionStore.closeTaskDrawer();

                  nextTick(() =>
                    router.push({
                      name: 'customers.customer',
                      params: { id: partnerId },
                    }),
                  );
                }
              "
            ></i>
          </PartnerQuickView>
        </div>
      </div>

      <div
        v-if="componentTask?.isCompleted"
        class="flex flex-nowrap justify-end items-center space-x-2.5"
      >
        <slot name="task_completed_actions" />
      </div>

      <div v-if="componentPartner?.id" class="flex flex-nowrap items-center space-x-2.5">
        <div class="space-x-1.5">
          <span>Open Invoices</span>
          <strong>{{ componentPartner?.num_open_invoices }}</strong>
        </div>

        <div class="space-x-1.5">
          <span>Total Balance</span>

          <MonetaryText
            class="font-bold"
            :value="componentPartner?.invoice_balance"
            :currency="componentPartner?.currency || 'USD'"
          />
        </div>

        <div class="space-x-1.5">
          <span>Overdue</span>

          <MonetaryText
            class="text-primary font-bold"
            :value="componentPartner?.total_overdue"
            :currency="componentPartner?.currency || 'USD'"
          />
        </div>

        <div class="flex items-center">
          <Button variant="ghost" size="icon-xs" @click="emits('toggleFullscreen')">
            <i class="fas" :class="isExpanded ? 'fa-compress' : 'fa-expand'" />
          </Button>

          <Button variant="ghost" size="icon-xs" @click="taskCollectionStore.closeTaskDrawer">
            <i class="fal fa-close text-base!" />
          </Button>
        </div>
      </div>
    </template>

    <template v-else-if="isTaskTypeGroupView">
      <strong>Bulk Action</strong>

      <Button variant="ghost" size="icon-xs" @click="emits('toggleFullscreen')">
        <i class="fas" :class="isExpanded ? 'fa-compress' : 'fa-expand'" />
      </Button>
    </template>
  </div>
</template>

<style></style>
